/**
 * Availability-import upload/preview/confirm DTOs and BullMQ queue-name
 * constant. Shapes derived from `availability-import-pipeline/spec.md`'s
 * "Upload and parse", "Profile resolution and preview", and "Confirm applies
 * an edited profile and enqueues the worker job" requirements — this
 * contracts+db task defines the DTOs; the NestJS routes land in the api
 * boundary's own task per `packages/contracts/AGENTS.md`'s four-step rule.
 *
 * Traceability: availability-import-pipeline / Requirement: Upload and parse,
 * Profile resolution and preview, Confirm applies an edited profile and
 * enqueues the worker job.
 */
import { z } from 'zod';
import { AvailabilityProfileSchema, isoDateSchema } from './availability-profile.js';
import { AvailabilityWindowStateSchema } from './availability-window.js';
import { ImportJobStatusSchema } from './import-job.js';

/** BullMQ queue name shared by the api (enqueue) and worker (processor). */
export const AVAILABILITY_IMPORT_QUEUE_NAME = 'availability-import';

/**
 * The operator's optional manual header-row override (gap 1, CU-86cb1npz2) —
 * shared by `preview` (query param) and `resolve-fresh` (JSON body field).
 * `z.coerce.number()` handles both a query-string value and an
 * already-numeric JSON body value. Also the NestJS controller's
 * `createZodDto`-based validation DTO for both endpoints (review round 1) —
 * a malformed value now fails with a 400 instead of silently becoming `NaN`.
 */
export const AvailabilityImportHeaderRowIndexSchema = z.object({
  headerRowIndex: z.coerce.number().int().nonnegative().optional(),
});
export type AvailabilityImportHeaderRowIndexInput = z.infer<
  typeof AvailabilityImportHeaderRowIndexSchema
>;

/** One raw physical row of the uploaded sheet, completely unprocessed (no
 * header detection, no column dropping). `rowNumber` is 1-based, exactly as
 * a spreadsheet app would show it — see `AvailabilityImportUploadResponseSchema`'s
 * `rawRows` field. */
export const AvailabilityImportRawRowSchema = z.object({
  rowNumber: z.number(),
  cells: z.array(z.string()),
});
export type AvailabilityImportRawRow = z.infer<typeof AvailabilityImportRawRowSchema>;

export const AvailabilityImportUploadResponseSchema = z.object({
  importJobId: z.number(),
  headers: z.array(z.string()),
  sampleRows: z.array(z.record(z.string(), z.string())),
  columnSignature: z.string(),
  /**
   * Total real data-row count for the whole file (unbounded) — the header-row
   * wizard step's "~N wierszy" summary MUST use this, not `rawRows.length`
   * (capped at ~20 for the raw physical-row picker, see `rawRows` below) or
   * it silently shows a misleadingly small count for any file over the cap
   * (review round 1, CU-86cb1npz2).
   */
  totalRows: z.number(),
  /**
   * Full-file (bounded) distinct non-empty values per detected column —
   * mirrors the media importer's `columnDistinctValues` (see
   * column-mapping-api spec). Unlike `sampleRows` (capped at
   * SAMPLE_ROWS_LIMIT rows), this covers every distinct value in the whole
   * file so the period-label mapping table can't miss a phrase that only
   * appears after the sample window. Optional: omitted for a column once it
   * exceeds the bounded unique-value limit.
   */
  columnDistinctValues: z.record(z.string(), z.array(z.string())).optional(),
  /**
   * First ~20 physical rows of the uploaded sheet, completely unprocessed
   * (no header detection, no column dropping) — feeds the manual
   * header-row-picker wizard step so an operator can override
   * `detectHeaderRow`'s automatic pick when it guessed wrong (e.g. a
   * cover-note/banner row above the real table). See gap 1, CU-86cb1npz2.
   */
  rawRows: z.array(AvailabilityImportRawRowSchema),
  /**
   * 0-based index into `rawRows` that `detectHeaderRow` picked
   * automatically — the manual header-row picker's initial selection
   * before any operator override.
   */
  detectedHeaderRowIndex: z.number().int().nonnegative(),
});
export type AvailabilityImportUploadResponse = z.infer<
  typeof AvailabilityImportUploadResponseSchema
>;

export const AvailabilityImportMatchMethodSchema = z.enum([
  'grw_id',
  'supplier_number',
  'gps',
  'fuzzy_address',
  'unmatched',
]);
export type AvailabilityImportMatchMethod = z.infer<typeof AvailabilityImportMatchMethodSchema>;

export const AvailabilityImportPreviewRowSchema = z.object({
  rowNumber: z.number(),
  matchedUnitId: z.number().nullable(),
  matchMethod: AvailabilityImportMatchMethodSchema,
  period: z.object({ start: isoDateSchema, end: isoDateSchema }),
  state: AvailabilityWindowStateSchema,
});
export type AvailabilityImportPreviewRow = z.infer<typeof AvailabilityImportPreviewRowSchema>;

export const AvailabilityImportPreviewResponseSchema = z.object({
  profile: AvailabilityProfileSchema,
  confidence: z.number(),
  /**
   * The actual per-column header labels used to parse this response — the
   * operator-overridden header row when `headerRowIndex` was given,
   * `detectHeaderRow`'s auto-detected row otherwise. The web preview's
   * column-mapping dropdowns MUST build their option lists from this field,
   * never from the upload response's `headers` (computed once at upload
   * time against the auto-detected row, and stale after a header-row
   * override — see CU-86cb1npz2 QA bugfix round 1).
   */
  headers: z.array(z.string()),
  /**
   * Sample data rows (capped, same convention as
   * `AvailabilityImportUploadResponseSchema.sampleRows`) parsed against the
   * SAME actually-used header row as `headers` above. The web preview's
   * "Przykład z pliku" column-mapping sample values and
   * `StatusTokenMappingTable`'s unmapped-token derivation MUST build from
   * this field, never from the upload response's `sampleRows` — that field
   * is captured once at upload time against the auto-detected row and goes
   * stale after a header-row override, silently keying every cell lookup
   * by the wrong row's headers (QA bugfix round 2, CU-86cb1npz2).
   */
  sampleRows: z.array(z.record(z.string(), z.string())),
  /**
   * Full-file (bounded) distinct non-empty values per column, same
   * convention and staleness caveat as `sampleRows` above — see
   * `AvailabilityImportUploadResponseSchema.columnDistinctValues`.
   */
  columnDistinctValues: z.record(z.string(), z.array(z.string())).optional(),
  rows: z.array(AvailabilityImportPreviewRowSchema),
  /**
   * Which tier resolved `profile` — `'remembered'` for a tier-1
   * `availability_profile_mappings` signature hit, `'resolved'` for tiers
   * 2-3 (`resolveAvailabilityProfile`). Lets the web preview tell the
   * operator this wasn't a fresh recognition (see
   * availability-profile-resolution's "Saved profile lookup by column
   * signature" requirement).
   */
  mappingSource: z.enum(['remembered', 'resolved']),
  /**
   * ISO datetime string equal to the matched mapping row's `updatedAt` when
   * `mappingSource = 'remembered'`; `null` when `mappingSource = 'resolved'`
   * since no saved mapping was consulted.
   */
  mappingSavedAt: z.string().datetime().nullable(),
});
export type AvailabilityImportPreviewResponse = z.infer<
  typeof AvailabilityImportPreviewResponseSchema
>;

export const AvailabilityImportConfirmRequestSchema = z.object({
  profile: AvailabilityProfileSchema,
  /**
   * Only meaningful when the confirmed profile is `timeEncoding = 'global'`
   * and `polarity = 'list_of_free'` — see availability-import-pipeline's
   * "Optional mark-rest-unavailable for global list-of-free files". The
   * confirm endpoint rejects this flag server-side when set outside that
   * scope rather than trusting the client to enforce it.
   */
  markRestUnavailable: z.boolean().optional().default(false),
});
export type AvailabilityImportConfirmRequest = z.infer<
  typeof AvailabilityImportConfirmRequestSchema
>;

/**
 * POST /availability-import/:id/rematch — recomputes per-row matching
 * (`matchedUnitId`/`matchMethod`) against a client-edited `AvailabilityProfile`
 * that has not yet been confirmed. Read-only companion to `preview`: the
 * initial `GET /preview` call resolves matching once server-side and the web
 * preview phase never re-derives `matchedUnitId` from edited
 * `idColumns.supplierNumberColumn`/`defaultSupplierName`/`supplierColumn`
 * client-side (see availability-import-ui/spec.md), so this endpoint lets the
 * operator re-run just the matching step after correcting the mapping,
 * without confirming (writing) anything.
 *
 * Traceability: availability-import-pipeline / Requirement: Profile
 * resolution and preview.
 */
export const AvailabilityImportRematchRequestSchema = z.object({
  profile: AvailabilityProfileSchema,
});
export type AvailabilityImportRematchRequest = z.infer<
  typeof AvailabilityImportRematchRequestSchema
>;

export const AvailabilityImportRematchRowSchema = z.object({
  rowNumber: z.number(),
  matchedUnitId: z.number().nullable(),
  matchMethod: AvailabilityImportMatchMethodSchema,
});
export type AvailabilityImportRematchRow = z.infer<typeof AvailabilityImportRematchRowSchema>;

export const AvailabilityImportRematchResponseSchema = z.object({
  rows: z.array(AvailabilityImportRematchRowSchema),
});
export type AvailabilityImportRematchResponse = z.infer<
  typeof AvailabilityImportRematchResponseSchema
>;

export const AvailabilityImportConfirmResponseSchema = z.object({
  ok: z.literal(true),
  importJobId: z.number(),
});
export type AvailabilityImportConfirmResponse = z.infer<
  typeof AvailabilityImportConfirmResponseSchema
>;

/**
 * Result-phase status DTO (task 4.3). Mirrors `ImportJobSchema`'s shape but
 * scoped to the three availability-import result buckets (no `errorRows`,
 * `totalRows`, or photo-bundle `resultSummary` — those are media-import-only
 * concepts) plus `errorMessage`, a server-derived human-readable message for
 * a whole-job failure (see `availability-import.job.ts`'s `errorLog: [{row:
 * 0, field: 'job', ...}]` failure convention) so the result phase can show a
 * distinct failure state with the actual error, not a false 0/0/0 success.
 */
export const AvailabilityImportStatusResponseSchema = z.object({
  id: z.number(),
  status: ImportJobStatusSchema,
  updatedRows: z.number().nullable(),
  unchangedRows: z.number().nullable(),
  unmatchedRows: z.number().nullable(),
  errorMessage: z.string().nullable(),
});
export type AvailabilityImportStatusResponse = z.infer<
  typeof AvailabilityImportStatusResponseSchema
>;
